/**
 * A dashboard's or a saved report's filters, in and out of its URL.
 *
 * The URL is where a viewer's filters are LOADED from and SAVED to — that is
 * what makes a filtered view bookmarkable and shareable, and what keeps a
 * viewer's filter from ever being saved for everybody. Both surfaces follow the
 * same rule (docs/reporting/requirements.md FR-4.8): the bar is the viewer's,
 * and the saved filters change only by an explicit action in edit mode.
 *
 * `field:key,key` for `is any of`, exactly as links were written before
 * filters had operators, so every link already shared still opens filtered.
 * Any other operator is `field~op:value`, the value being the list for
 * `is none of` and JSON otherwise. `|` between rules, in bar order.
 *
 * A bar holding an either/or GROUP — the report builder can make one — has no
 * spelling in that grammar, so such a bar is written whole as `json:` and the
 * node list. Only then: the common case stays something a person can read and
 * edit. Anything malformed is DROPPED rather than thrown: a shared link that
 * picked up a stray character should open unfiltered, not refuse to open.
 */
import {
  FILTER_OPS,
  type FilterGroup,
  type FilterNode,
  type FilterOp,
  type FilterRule,
  type FilterValue,
  filterNodeSchema,
  filterValueSchema,
  isFilterGroup,
  OP_ARITY,
} from '@clockwise/semantic-core';
import { z } from 'zod';

/**
 * Does this rule SAY something, whatever model it meets?
 *
 * ONE answer, read by the Clear button, the tile notices, the URL and the
 * tiles, so the bar cannot claim a filter is on that nothing runs. A chip
 * still waiting for its first value, an emptied list and a range open at both
 * ends all narrow nothing. A group is never a rule — see {@link keep} for when
 * one is kept.
 */
export function saysSomething(node: FilterNode): node is FilterRule {
  if (isFilterGroup(node) || node.paused) return false;
  const value = node.value;
  if (value === undefined) return OP_ARITY[node.op] === 'none';
  if (value.kind === 'list') return value.values.length > 0;
  if (value.kind === 'numberRange') return value.min !== null || value.max !== null;
  if (value.kind === 'dateRange') return value.from !== null || value.to !== null;
  return true;
}

/**
 * The bar as a link would carry it: rules that say something, and groups cut
 * down to the children that do. A group left with one child is that child —
 * an "either" of one thing is the thing.
 */
function keep(filters: readonly FilterNode[]): FilterNode[] {
  return filters.flatMap((node): FilterNode[] => {
    if (!isFilterGroup(node)) return saysSomething(node) ? [node] : [];
    const children = node.children.filter(saysSomething);
    if (children.length >= 2) return [{ ...node, children }];
    return children;
  });
}

const JSON_PREFIX = 'json:';

const LIST_OPS: readonly FilterOp[] = ['in', 'not_in'];

/**
 * Rules out of a URL.
 *
 * Ids are made up here — `field`, then `field-2` for a second rule on the same
 * column — because a link does not carry them. They are stable for a given
 * link, which is all a rule's id has to be.
 */
export function parseFilters(encoded?: string): FilterNode[] {
  if (!encoded) return [];
  if (encoded.startsWith(JSON_PREFIX)) return parseJson(encoded.slice(JSON_PREFIX.length));
  const out: FilterRule[] = [];
  const perField = new Map<string, number>();

  for (const part of encoded.split('|')) {
    const at = part.indexOf(':');
    if (at <= 0) continue;
    const head = safeDecode(part.slice(0, at));
    const payload = part.slice(at + 1);
    if (head === null) continue;

    const tilde = head.indexOf('~');
    const field = tilde < 0 ? head : head.slice(0, tilde);
    const op = (tilde < 0 ? 'in' : head.slice(tilde + 1)) as FilterOp;
    if (!field || !(FILTER_OPS as readonly string[]).includes(op)) continue;

    const value = parseValue(op, payload);
    if (value === null) continue;

    const n = (perField.get(field) ?? 0) + 1;
    perField.set(field, n);
    out.push({ id: n === 1 ? field : `${field}-${n}`, field, op, value, paused: false });
  }
  return out;
}

/** `undefined` for an operator that takes no value; `null` for a part to drop. */
function parseValue(op: FilterOp, payload: string): FilterValue | undefined | null {
  if (LIST_OPS.includes(op)) {
    const values = payload
      .split(',')
      .filter(Boolean)
      .map(safeDecode)
      .filter((v): v is string => v !== null);
    return values.length > 0 ? { kind: 'list', values } : null;
  }
  if (OP_ARITY[op] === 'none') return payload ? null : undefined;
  const text = safeDecode(payload);
  if (!text) return null;
  try {
    const parsed = filterValueSchema.safeParse(JSON.parse(text));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Rules into a URL — only the ones that SAY something.
 *
 * A chip still waiting for its first value, a paused rule and a range open at
 * both ends are all left out: none of them narrows anything. `undefined` when
 * nothing is left, so the parameter disappears.
 */
export function encodeFilters(filters: readonly FilterNode[]): string | undefined {
  const kept = keep(filters);
  if (kept.length === 0) return undefined;
  if (kept.some(isFilterGroup))
    return JSON_PREFIX + encodeURIComponent(JSON.stringify(kept.map(bare)));
  const parts: string[] = [];
  for (const rule of kept as FilterRule[]) {
    const field = encodeURIComponent(rule.field);
    const head = rule.op === 'in' ? field : `${field}~${rule.op}`;
    const value = rule.value;
    if (value?.kind === 'list') {
      parts.push(`${head}:${value.values.map(encodeURIComponent).join(',')}`);
    } else {
      parts.push(`${head}:${value ? encodeURIComponent(JSON.stringify(value)) : ''}`);
    }
  }
  return parts.join('|');
}

/**
 * A node without its ids, for the `json:` form.
 *
 * Ids name chips on ONE screen; a link has no use for them, and keeping them
 * would make two links to the same view differ — which is exactly the
 * comparison `writeFilters` makes.
 */
function bare(node: FilterNode): unknown {
  if (isFilterGroup(node)) return { join: node.join, children: node.children.map(bare) };
  return { field: node.field, op: node.op, ...(node.value && { value: node.value }) };
}

/** The `json:` form back into nodes, with ids made up as `parseFilters` makes them. */
function parseJson(payload: string): FilterNode[] {
  const text = safeDecode(payload);
  if (!text) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];
  const perId = new Map<string, number>();
  const idFor = (base: string) => {
    const n = (perId.get(base) ?? 0) + 1;
    perId.set(base, n);
    return n === 1 ? base : `${base}-${n}`;
  };
  const withIds = (node: Record<string, unknown>): unknown =>
    Array.isArray(node.children)
      ? {
          ...node,
          id: idFor('group'),
          children: node.children.map((c) => withIds(c as Record<string, unknown>)),
        }
      : { ...node, id: idFor(String(node.field)), paused: false };
  const parsed = z
    .array(filterNodeSchema)
    .max(20)
    .safeParse(raw.map((node) => withIds((node ?? {}) as Record<string, unknown>)));
  return parsed.success ? (parsed.data as FilterNode[]) : [];
}

function safeDecode(text: string): string | null {
  try {
    return decodeURIComponent(text);
  } catch {
    return null;
  }
}

/**
 * The `f` a viewer who CLEARED the saved filters is left with.
 *
 * No `f` at all means "the dashboard's own filters", so emptying the bar on a
 * dashboard that opens filtered needs a value that says "none" out loud — or
 * the defaults come straight back on the next round trip. `parseFilters` reads
 * it as no rules (it has no `:`), so the word needs no special case there and
 * no field can collide with it.
 */
export const CLEARED_FILTERS = 'none';

/**
 * The filters a page opens on: the link's when it carries any, else the
 * saved ones.
 *
 * WHOLE, never merged. A link names the view it was copied from, and folding
 * the defaults into it would turn a link to "everyone" into "everyone in the
 * default roles" the day an editor sets a default.
 */
export function readFilters(
  encoded: string | undefined,
  defaults: readonly FilterNode[],
): FilterNode[] {
  if (encoded === undefined) return defaults.map((node) => ({ ...node }));
  return parseFilters(encoded);
}

/**
 * The `f` for a set of filters, against the saved ones.
 *
 * Absent when the rules ARE the defaults, so the plain link stays the plain
 * link and follows the defaults when an editor changes them. Compared as
 * encoded links rather than as objects: ids and paused rules never reach a URL,
 * so they cannot tell two views apart either.
 */
export function writeFilters(
  filters: readonly FilterNode[],
  defaults: readonly FilterNode[],
): string | undefined {
  const encoded = encodeFilters(filters);
  if (encoded === encodeFilters(defaults)) return undefined;
  return encoded ?? CLEARED_FILTERS;
}

/**
 * What "Use current filters … as default" stores: the bar, exactly as a link
 * would carry it.
 *
 * Round-tripped through the URL encoding on purpose. A saved filter the URL
 * cannot express — a paused rule, an empty chip — would open differently from
 * the link to the same view, and `writeFilters` could never recognise the view
 * as the saved one.
 */
export function toSavedFilters(filters: readonly FilterNode[]): FilterNode[] {
  return parseFilters(encodeFilters(filters));
}

/**
 * {@link toSavedFilters} for a dashboard, whose defaults are rules alone: its
 * bar has no way to make a group, so there is none to lose.
 */
export function toDefaultFilters(filters: readonly FilterNode[]): FilterRule[] {
  return toSavedFilters(filters).filter((node): node is FilterRule => !isFilterGroup(node));
}

export type { FilterGroup };
